import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Activity, CheckCircle2 } from 'lucide-react';
import { useGetUserStatisticsQuery } from './dashboardApi';

const formatGrowthPercentage = (percentage: number) => {
  const sign = percentage >= 0 ? '+' : '';
  return `${sign}${percentage}%`;
};

const getGrowthColor = (percentage: number) => {
  if (percentage > 0) return 'text-green-400';
  if (percentage < 0) return 'text-red-400';
  return 'text-slate-400';
};

export const Dashboard = () => {
  const { data: userStats, isLoading: isLoadingStats, error: statsError } = useGetUserStatisticsQuery();

  return (
    <>
      {/* Dashboard Overview */}
      <div className='mb-8'>
        <h2 className='text-3xl font-bold text-white mb-2'>Dashboard Overview</h2>
        <p className='text-slate-400'>Welcome to your admin dashboard. Manage your organization's data and settings.</p>
      </div>

      {/* Metrics Cards */}
      <div className='grid gap-6 md:grid-cols-3 mb-8'>
        <Card className='bg-slate-800 border-slate-700'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-slate-400'>Total Users</p>
                {isLoadingStats ? (
                  <div className='text-3xl font-bold text-slate-400'>Loading...</div>
                ) : statsError ? (
                  <div className='text-3xl font-bold text-red-400'>Error</div>
                ) : (
                  <>
                    <p className='text-3xl font-bold text-white'>{userStats?.totalCount?.toLocaleString() ?? 0}</p>
                    <p className={`text-sm ${getGrowthColor(userStats?.growthPercentage ?? 0)}`}>
                      {formatGrowthPercentage(userStats?.growthPercentage || 0)} from last month
                    </p>
                  </>
                )}
              </div>
              <Users className='h-8 w-8 text-slate-400' />
            </div>
          </CardContent>
        </Card>

        <Card className='bg-slate-800 border-slate-700'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-slate-400'>New This Month</p>
                {isLoadingStats ? (
                  <div className='text-3xl font-bold text-slate-400'>Loading...</div>
                ) : statsError ? (
                  <div className='text-3xl font-bold text-red-400'>Error</div>
                ) : (
                  <>
                    <p className='text-3xl font-bold text-white'>{userStats?.currentMonthCount?.toLocaleString() ?? 0}</p>
                    <p className='text-sm text-slate-400'>
                      {userStats?.lastMonthCount ?? 0} last month
                    </p>
                  </>
                )}
              </div>
              <Activity className='h-8 w-8 text-slate-400' />
            </div>
          </CardContent>
        </Card>

        <Card className='bg-slate-800 border-slate-700'>
          <CardContent className='p-6'>
            <div className='flex items-center justify-between'>
              <div>
                <p className='text-sm font-medium text-slate-400'>System Status</p>
                <p className='text-2xl font-bold text-green-400'>Healthy</p>
                <p className='text-sm text-slate-400'>All systems operational</p>
              </div>
              <CheckCircle2 className='h-8 w-8 text-green-400' />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <div className='grid gap-6 lg:grid-cols-2'>
        <Card className='bg-slate-800 border-slate-700'>
          <CardHeader>
            <CardTitle className='text-white'>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='flex items-center gap-3'>
              <div className='w-2 h-2 bg-green-400 rounded-full'></div>
              <div className='flex-1'>
                <p className='text-white text-sm'>User registration completed</p>
              </div>
              <span className='text-slate-400 text-sm'>2 min ago</span>
            </div>
            
            <div className='flex items-center gap-3'>
              <div className='w-2 h-2 bg-blue-400 rounded-full'></div>
              <div className='flex-1'>
                <p className='text-white text-sm'>Database backup completed</p>
              </div>
              <span className='text-slate-400 text-sm'>1 hour ago</span>
            </div>
            
            <div className='flex items-center gap-3'>
              <div className='w-2 h-2 bg-yellow-400 rounded-full'></div>
              <div className='flex-1'>
                <p className='text-white text-sm'>System maintenance scheduled</p>
              </div>
              <span className='text-slate-400 text-sm'>3 hours ago</span>
            </div>

            <div className='flex items-center gap-3'>
              <div className='w-2 h-2 bg-purple-400 rounded-full'></div>
              <div className='flex-1'>
                <p className='text-white text-sm'>New admin user created</p>
              </div>
              <span className='text-slate-400 text-sm'>4 hours ago</span>
            </div>

            <div className='flex items-center gap-3'>
              <div className='w-2 h-2 bg-orange-400 rounded-full'></div>
              <div className='flex-1'>
                <p className='text-white text-sm'>Security audit completed</p>
              </div>
              <span className='text-slate-400 text-sm'>6 hours ago</span>
            </div>
          </CardContent>
        </Card>

        <Card className='bg-slate-800 border-slate-700'>
          <CardHeader>
            <CardTitle className='text-white'>System Overview</CardTitle>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='flex items-center justify-between'>
              <span className='text-slate-300'>Server Status</span>
              <span className='text-green-400 font-medium'>Online</span>
            </div>
            <div className='flex items-center justify-between'>
              <span className='text-slate-300'>Database</span>
              <span className='text-green-400 font-medium'>Connected</span>
            </div>
            <div className='flex items-center justify-between'>
              <span className='text-slate-300'>API Status</span>
              <span className='text-green-400 font-medium'>Operational</span>
            </div>
            <div className='flex items-center justify-between'>
              <span className='text-slate-300'>Backup System</span>
              <span className='text-green-400 font-medium'>Active</span>
            </div>
            <div className='flex items-center justify-between'>
              <span className='text-slate-300'>Memory Usage</span>
              <span className='text-yellow-400 font-medium'>67%</span>
            </div>
            <div className='flex items-center justify-between'>
              <span className='text-slate-300'>Storage</span>
              <span className='text-blue-400 font-medium'>42% Used</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
